// Reconcile the on-screen chat list with the server's authoritative history,
// and — where a message on screen is provably the same message the server just
// persisted — carry its RENDER IDENTITY across so its bubble doesn't remount.
//
// The original rule only ever seeded when the displayed list was *empty*.
// That meant anything the server appended after the first render — voice-call
// turns, background/cron results, any append that didn't come from the user's
// own typed message — never painted until a full page reload (the reload
// reset the list to empty, which is the only state that re-seeded).
//
// Reconciling instead fixed that, but did it by handing the whole server array
// to `chat.setMessages`. The just-streamed message's id goes from the AI SDK's
// client-side synthetic one to its persisted `chat_messages.id`, and both the
// per-message wrapper key (`chat-messages.tsx`) and every tool card key
// (`message-bubble.tsx`) were derived from that id — so the end of every turn
// unmounted and rebuilt the just-streamed bubble. Anything holding
// mount-captured state inside it was destroyed: the generated-image lightbox
// (an uncontrolled Radix Dialog) closed itself (FRONT-835), and the reasoning
// panel re-opened and re-collapsed (FRONT-1857's flicker half).
//
// FRONT-1717 (PR 2). The message id STAYS the persisted row id — every
// consumer that needs a numeric `chat_messages.id` (retry, delete, hide, the
// fork-group anchor) keeps reading `message.id` exactly as before, and none of
// them changed. What moves is only what the TREE mounts on: a message carries
// a `metadata.renderKey`, minted from the id it first appeared under, and the
// two key sites read `renderKeyOf` instead of the raw id. So the id is free to
// be promoted while the render identity holds still.
//
// This is deliberately NOT the key fix the ticket ruled out. That fix failed
// because no stable key EXISTED to reach for: `message.id` changes on the
// promotion, and list position changes on a length change. Here the reconcile
// MANUFACTURES the stable value and is the only thing that may set it, which
// is why it belongs in this module and not in the components.
//
// `renderKey` rides in `metadata` on purpose. The AI SDK keeps metadata opaque
// across `setMessages`, and the chat-proxy's user-message insert writes only
// explicit columns — so a renderKey travelling upstream in a request body is
// never persisted and can never come back from `/history` as a stale value.
//
// What makes the carry-over safe is EVIDENCE, not position. A displayed
// message inherits its render identity onto a server row only when the row is
// the same row (ids match), or when all of these hold: the on-screen id is one
// the client minted, the server's is a persisted row id, the roles agree, and
// the text is identical. Anything less and the row is taken exactly
// as the server sent it — the pre-FRONT-1717 behaviour, i.e. that one bubble
// remounts. Getting it wrong is therefore cosmetic: a message can lose its
// mounted state, never its identity, and never another message's content.

/** Minimal structural shape — keeps this pure and unit-testable without
 *  pulling in the AI SDK's UIMessage type. `metadata` and `parts` are
 *  deliberately loose: the SDK parameterises both, and this module only reads
 *  a render key out of one and text out of the other. */
export interface IdentifiableMessage {
  id: string | number;
  role?: string;
  parts?: readonly unknown[];
  metadata?: unknown;
}

export interface ReconcileConditions {
  /** A history refetch is currently in flight (cached value is stale). */
  isFetching: boolean;
  /** A chat turn is actively streaming/submitting (don't clobber it). */
  isStreaming: boolean;
  /**
   * FRONT-1728: the person pressed Stop on the turn on screen. Their copy of
   * that reply is exactly what they saw, and the server's copy of it is, until
   * the turn settles, a progress snapshot up to 5 seconds older — often still
   * empty. Without this the reconcile Stop itself triggers swapped the reply
   * they were reading for that snapshot and it vanished. See the rule below.
   */
  stoppedLocally?: boolean;
}

/**
 * `none` — the list already agrees with the server; touch nothing.
 * `apply` — hand `messages` to `setMessages`. It is always the server's list,
 *           in the server's order, with the server's ids; what it adds is the
 *           carried render keys and the reuse of untouched message objects.
 */
export type ReconcileResult<T> = { action: 'none' } | { action: 'apply'; messages: T[] };

const NONE = { action: 'none' } as const;

/** A persisted `chat_messages.id`, which the history endpoint serialises as a
 *  decimal string (`String(r.id)` in `chat-proxy.ts`, over a `serial` primary
 *  key). Anything else on screen is an id the AI SDK minted for a message it
 *  has not yet seen persisted. */
const PERSISTED_ID_RE = /^\d+$/;

function isClientMintedId(id: string | number): boolean {
  return !PERSISTED_ID_RE.test(String(id));
}

/**
 * What this message's subtree should be mounted on.
 *
 * Read this — never `message.id` — for a React key anywhere in the chat
 * message tree. Read `message.id` for everything else: it is still the
 * persisted row id, and nothing about that changed.
 */
export function renderKeyOf(message: IdentifiableMessage): string | undefined {
  const meta = message.metadata;
  if (meta && typeof meta === 'object' && 'renderKey' in meta) {
    const value = (meta as { renderKey?: unknown }).renderKey;
    if (typeof value === 'string' && value.length > 0) return value;
  }
  return message.id != null ? String(message.id) : undefined;
}

/** Concatenated text of a message's text parts, trimmed. The comparison used
 *  to decide two entries are the same message. */
function textOf(message: IdentifiableMessage): string {
  const parts = message.parts;
  if (!Array.isArray(parts)) return '';
  let out = '';
  for (const part of parts) {
    if (part && typeof part === 'object') {
      const p = part as { type?: unknown; text?: unknown };
      if (p.type === 'text' && typeof p.text === 'string') out += p.text;
    }
  }
  return out.trim();
}

/**
 * May `displayed` hand its render identity to `server`?
 *
 * Evidence only — deliberately NOT position. The text comparison is the real
 * guard: it is what stops a concurrent writer's row (a cron result, a voice
 * turn, another tab) inheriting a bubble the viewer believes is theirs, in the
 * case where the list lengths coincide and the roles happen to match.
 *
 * An earlier revision also demanded that the two lists have the same number of
 * entries left from this point on. That was dropped: it is not the guard it
 * looks like — an append AFTER the promoted message breaks the count while
 * being entirely benign, so it discarded the render key in exactly the
 * overlapping-turn case this is supposed to protect. The caller's forward-only
 * walk already prevents pairing backwards, and consumes each displayed message
 * at most once, so no two rows can be handed the same render key.
 *
 * Residual, accepted: two same-role messages whose text is identical (both
 * empty, for a tool-only turn) can pair with each other. The cost is that one
 * bubble keeps mounted state that belonged to the other — cosmetic, which is
 * the whole point of moving this off the id. It can never move CONTENT or a
 * row id, because the server's row is always taken exactly as sent.
 */
function isSameTurnSlot(displayed: IdentifiableMessage, server: IdentifiableMessage): boolean {
  if (!isClientMintedId(displayed.id)) return false;
  if (isClientMintedId(server.id)) return false;
  if (displayed.role !== server.role) return false;
  return textOf(displayed) === textOf(server);
}

/**
 * FRONT-2195: is this on-screen row a `/history` snapshot of an assistant turn
 * the server itself reported as NOT finished yet?
 *
 * The server stamps `metadata.streamStatus` on exactly the rows it has not
 * finalised (`streamLifecycleMeta`, src/agents/stream-lifecycle-meta.ts — a
 * `status="streaming"` row, live or orphaned, and a sweeper-declared
 * `status="interrupted"` one) and omits it once the row is final. A streaming
 * row is finalised IN PLACE — same `chat_messages.id`, new parts — so a
 * displayed copy of it is a snapshot the server will later supersede under
 * the very id the same-row rule below matches on.
 */
function isUnfinishedServerSnapshot(message: IdentifiableMessage): boolean {
  const meta = message.metadata;
  return (
    !!meta &&
    typeof meta === 'object' &&
    typeof (meta as { streamStatus?: unknown }).streamStatus === 'string'
  );
}

/** Everything about a message a newer server copy could change — role,
 *  parts and metadata — minus the render key, which only this module sets
 *  and the server never sends. */
function contentOf(message: IdentifiableMessage): string {
  let meta = message.metadata;
  if (meta && typeof meta === 'object' && 'renderKey' in meta) {
    const { renderKey: _renderKey, ...rest } = meta as Record<string, unknown>;
    // `withRenderKey` turns an absent metadata into `{ renderKey }` — with the
    // key gone that is `{}`, which must read the same as "no metadata".
    meta = Object.keys(rest).length > 0 ? rest : undefined;
  }
  return JSON.stringify([message.role ?? null, message.parts ?? null, meta ?? null]);
}

function withRenderKey<T extends IdentifiableMessage>(server: T, renderKey: string): T {
  const meta = server.metadata && typeof server.metadata === 'object' ? server.metadata : {};
  return { ...server, metadata: { ...meta, renderKey } };
}

/**
 * Decide what to do with the server's history, and build the list to apply.
 *
 * Rules:
 * - Never while fetching or streaming.
 * - Never seed from an empty/absent history (an empty or wrong-branch fetch
 *   must not wipe a populated thread).
 * - Never revert to a SHORTER server snapshot — the post-stream window, where
 *   the local list is legitimately ahead. Server-side shrinks like
 *   compress/clear go through an explicit reload(), not this effect.
 * - Otherwise the server's list wins, with render identity carried across
 *   wherever a message is provably the same one, and untouched message objects
 *   reused by reference so their subtrees don't even re-render.
 * - A same-id row is reused as-is EXCEPT an unfinished-turn snapshot whose
 *   server copy has changed (FRONT-2195): that one takes the server's copy,
 *   keeping its render identity. A finished row is never replaced this way.
 * - If that produces exactly what is already on screen, do nothing. This is
 *   what makes one turn settle cause one reconcile: after applying, every id
 *   matches, so every message is reused and the next pass is a no-op.
 */
export function reconcileHistory<T extends IdentifiableMessage>(
  displayed: readonly T[],
  history: readonly T[] | undefined,
  { isFetching, isStreaming, stoppedLocally = false }: ReconcileConditions,
): ReconcileResult<T> {
  if (isFetching || isStreaming) return NONE;
  if (!history || history.length === 0) return NONE;
  // Local is AHEAD of the server: a turn just finished streaming and the
  // post-stream history refetch hasn't landed yet. Never replace the fresher
  // local messages with a shorter server snapshot — that would blink the
  // just-streamed reply out and back in.
  if (history.length < displayed.length) return NONE;

  const merged: T[] = [];
  let d = 0;
  for (let h = 0; h < history.length; h++) {
    const server = history[h];
    // A hole in the server's array (the query only checks `Array.isArray`, and
    // a JSON array can legitimately contain null) would otherwise throw while
    // reading `.id`, from inside a render effect. Refuse the whole reconcile
    // instead: the alignment this walk depends on cannot be trusted, and
    // leaving the screen untouched is recoverable where a partial application
    // — or a crash — is not.
    if (!server) return NONE;
    const cand = displayed[d];
    if (
      stoppedLocally &&
      cand &&
      isClientMintedId(cand.id) &&
      !isClientMintedId(server.id) &&
      cand.role === 'assistant' &&
      server.role === 'assistant' &&
      isUnfinishedServerSnapshot(server)
    ) {
      // FRONT-1728: the reply the person just stopped, against the server's
      // still-unfinished snapshot of the same turn. Keep what they saw. Once
      // the server finalises the row it is no longer a snapshot, and the
      // ordinary rules take it (with the "seen" parts it then carries).
      merged.push(cand);
      d++;
      continue;
    }
    if (
      cand &&
      String(cand.id) === String(server.id) &&
      isUnfinishedServerSnapshot(cand) &&
      contentOf(cand) !== contentOf(server)
    ) {
      // FRONT-2195: an unfinished-turn snapshot is the one same-id row the
      // server rewrites in place (the streaming row is finalised under its
      // own id), so for it "same id" does not mean "agreed". Keeping the
      // displayed object there froze a finished answer behind its last
      // mid-turn snapshot forever: a chat window that did not stream the
      // finish itself (a second hook instance on the same session got it)
      // saw the final row arrive in `/history`, matched it by id, kept the
      // stale copy, and never asked again. So a changed server copy of an
      // unfinished snapshot wins — content, metadata and all — while the
      // render identity it was mounted under rides across. A FINISHED
      // displayed row never takes this branch: a late or stale snapshot can
      // never revert an answer that is already final.
      const key = renderKeyOf(cand);
      merged.push(key && key !== String(server.id) ? withRenderKey(server, key) : server);
      d++;
      continue;
    }
    if (cand && String(cand.id) === String(server.id)) {
      // Same row, already on screen. Keep the exact object so its subtree
      // neither remounts nor re-renders; any renderKey it carries rides along.
      merged.push(cand);
      d++;
      continue;
    }
    if (cand && isSameTurnSlot(cand, server)) {
      // The promotion. Take the server's row wholesale — its id, content and
      // metadata — and carry only the render identity across.
      const key = renderKeyOf(cand);
      merged.push(key ? withRenderKey(server, key) : server);
      d++;
      continue;
    }
    // A server row with nothing on screen to match it: an append, or a row the
    // server ordered among messages we already show. Take it as sent.
    merged.push(server);
  }

  const unchanged =
    merged.length === displayed.length && merged.every((m, i) => m === displayed[i]);
  return unchanged ? NONE : { action: 'apply', messages: merged };
}
